import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Loader2, Wand2, Camera, Calendar, Clock, MapPin, User as UserIcon,
  PartyPopper, Download, Share2, Edit3, Check, ArrowLeft, RefreshCw, Lock, Zap,
} from "lucide-react";
import { API_URL, LOGO_URL } from "../constants";
import LeadCaptureCard from "../components/LeadCaptureCard";
import EarlyBirdStrip from "../components/EarlyBirdStrip";
import AIImageWithDisclaimer from "../components/AIImageWithDisclaimer";
import { bakeDisclaimerOnBase64 } from "../utils/aiDisclaimer";

const OCCASIONS = [
  "Birthday", "Baby Shower", "Anniversary", "Engagement", "Wedding",
  "Housewarming", "Festival", "Bachelorette", "Bachelor", "Retirement",
];
const STYLES = ["Elegant", "Floral", "Minimal", "Royal", "Boho", "Modern"];
const DEFAULT_FIELDS = {
  host_name: "", occasion: "Birthday", date: "", time: "",
  venue: "", note: "", style: "Elegant",
};

const AIInvitationsPage = () => {
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [photoB64, setPhotoB64] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);    // { id, image_base64, free_remaining, paid_credits, is_booked }
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const [paywall, setPaywall] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const fileRef = useRef();

  // Load usage on mount
  useEffect(() => {
    fetch(`${API_URL}/api/invitations/ai/usage`, { credentials: "include" })
      .then(r => r.json()).then(setUsage).catch(() => {});
  }, []);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError("Photo too large (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPhotoPreview(dataUrl);
      setPhotoB64(dataUrl.split(",")[1]);
    };
    reader.readAsDataURL(f);
  };

  const callGenerate = async (alterId = null) => {
    setError("");
    if (!fields.host_name.trim()) { setError("Please enter the host name"); return; }
    if (!fields.occasion.trim()) { setError("Please pick an occasion"); return; }
    setGenerating(true);
    try {
      const url = alterId
        ? `${API_URL}/api/invitations/ai/alter/${alterId}`
        : `${API_URL}/api/invitations/ai/generate`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields, photo_b64: photoB64 || undefined }),
      });
      if (res.status === 402) {
        setPaywall(true);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      // Refresh usage
      fetch(`${API_URL}/api/invitations/ai/usage`, { credentials: "include" })
        .then(r => r.json()).then(setUsage).catch(() => {});
    } catch (e) {
      setError(e.message || "Could not generate invitation");
    } finally {
      setGenerating(false);
    }
  };

  const buyCredits = async (amount) => {
    setPurchasing(true);
    try {
      const orderRes = await fetch(`${API_URL}/api/invitations/ai/credits/order`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount }),
      });
      if (!orderRes.ok) {
        const d = await orderRes.json().catch(() => ({}));
        throw new Error(d?.detail || `HTTP ${orderRes.status}`);
      }
      const order = await orderRes.json();
      // Open Razorpay
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount * 100,
        currency: "INR",
        order_id: order.razorpay_order_id,
        name: "DoozyGo® Invitations",
        description: `${order.credits} alteration credit${order.credits > 1 ? "s" : ""}`,
        handler: async (response) => {
          const verify = await fetch(`${API_URL}/api/invitations/ai/credits/verify`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          if (verify.ok) {
            setPaywall(false);
            const u = await fetch(`${API_URL}/api/invitations/ai/usage`, { credentials: "include" }).then(r => r.json());
            setUsage(u);
          } else {
            setError("Payment verification failed. Please contact support.");
          }
        },
        theme: { color: "#E83E8C" },
      });
      rzp.open();
    } catch (e) {
      setError(e.message);
    } finally {
      setPurchasing(false);
    }
  };

  const downloadImage = async () => {
    if (!result?.image_base64) return;
    const dataUrl = await bakeDisclaimerOnBase64(result.image_base64, "image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `doozygo-invitation-${fields.occasion.replace(/\s+/g, "_")}-${result.id?.slice(0, 6) || "card"}.png`;
    a.click();
  };

  const shareWhatsapp = () => {
    if (!result?.image_base64) return;
    const text = `🎉 You're invited to my ${fields.occasion}! Designed with DoozyGo® AI ✨`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + "\n\nMore info: " + window.location.origin)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF7F2] via-white to-[#FFE5F0]" data-testid="ai-invitations-page">
      <EarlyBirdStrip />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1E1B4B] hover:text-[#E83E8C] transition-colors">
            <ArrowLeft size={18} /> <img src={LOGO_URL} alt="DoozyGo" className="h-7" />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/my-account?tab=creations" className="text-xs text-[#475569] hover:text-[#E83E8C] flex items-center gap-1">
              <Sparkles size={12} /> My Creations
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E83E8C]/10 border border-[#E83E8C]/20 mb-3">
            <Wand2 size={12} className="text-[#E83E8C]" />
            <span className="text-[10px] font-bold tracking-widest text-[#E83E8C] uppercase">DoozyGo® Magic AI</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-[#1E1B4B] mb-2 leading-tight">
            Design Your Invitation<br />
            <span className="bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] bg-clip-text text-transparent">in 30 Seconds</span>
          </h1>
          <p className="text-sm sm:text-base text-[#475569] max-w-xl mx-auto">
            Add a photo, fill in the details, let our AI craft a beautiful card. Free preview, then 2 free alterations for booked customers.
          </p>
        </div>

        {/* Usage banner */}
        {usage && (
          <div className="max-w-2xl mx-auto mb-6 px-4 py-3 rounded-2xl bg-white border border-gray-200 flex items-center justify-between text-xs sm:text-sm">
            <div className="flex items-center gap-3">
              <span className="font-bold text-[#1E1B4B]">
                {usage.free_remaining > 0
                  ? `${usage.free_remaining} free ${usage.free_remaining === 1 ? "preview" : "previews"} left`
                  : usage.paid_credits > 0
                    ? `${usage.paid_credits} paid alteration${usage.paid_credits !== 1 ? "s" : ""} available`
                    : "Free quota used"}
              </span>
              {usage.is_booked && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 text-[10px] font-bold">
                  ✓ BOOKED CUSTOMER
                </span>
              )}
            </div>
            {usage.free_remaining === 0 && usage.paid_credits === 0 && (
              <button onClick={() => setPaywall(true)} className="text-[10px] sm:text-xs font-bold text-[#E83E8C] hover:underline" data-testid="open-paywall-btn">
                Buy credits →
              </button>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* LEFT: Form */}
          {!result && (
            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-7 space-y-4">
              <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2">
                <Edit3 size={16} className="text-[#E83E8C]" /> Tell us about your party
              </h3>

              {/* Photo upload */}
              <div>
                <label className="text-xs font-bold text-[#475569] block mb-1.5">Photo (optional — recommended)</label>
                {photoPreview ? (
                  <div className="relative w-full h-40 rounded-2xl overflow-hidden border-2 border-[#E83E8C]/30">
                    <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => { setPhotoPreview(""); setPhotoB64(""); }}
                      className="absolute top-2 right-2 px-2 py-1 rounded-lg bg-black/70 text-white text-[10px] font-bold">
                      Change
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()}
                    className="w-full h-40 rounded-2xl border-2 border-dashed border-gray-300 hover:border-[#E83E8C] hover:bg-[#E83E8C]/5 transition-all flex flex-col items-center justify-center gap-2 text-[#475569]"
                    data-testid="invitation-photo-upload">
                    <Camera size={28} className="text-[#E83E8C]" />
                    <span className="text-sm font-bold">Upload a photo</span>
                    <span className="text-[10px]">JPG/PNG · max 5MB</span>
                  </button>
                )}
                <input type="file" ref={fileRef} accept="image/*" onChange={handlePhoto} className="hidden" />
              </div>

              {/* Host name */}
              <div>
                <label className="text-xs font-bold text-[#475569] block mb-1.5"><UserIcon size={11} className="inline mr-1" /> Host Name *</label>
                <input value={fields.host_name} onChange={(e) => setFields({...fields, host_name: e.target.value})}
                  placeholder="e.g. Aarav & Priya"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
                  data-testid="invitation-host-input" maxLength={80} />
              </div>

              {/* Occasion */}
              <div>
                <label className="text-xs font-bold text-[#475569] block mb-1.5"><PartyPopper size={11} className="inline mr-1" /> Occasion *</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {OCCASIONS.map(o => (
                    <button key={o} onClick={() => setFields({...fields, occasion: o})}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${fields.occasion === o ? "bg-[#E83E8C] text-white shadow-md" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}
                      data-testid={`occasion-${o.replace(/\s+/g, '-')}`}>
                      {o}
                    </button>
                  ))}
                </div>
                <input value={fields.occasion.startsWith("Custom: ") ? fields.occasion.slice(8) : ""} onChange={(e) => setFields({...fields, occasion: e.target.value ? `Custom: ${e.target.value}` : "Birthday"})}
                  placeholder="Or type a custom occasion…"
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs" maxLength={60} />
              </div>

              {/* Date + Time row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[#475569] block mb-1.5"><Calendar size={11} className="inline mr-1" /> Date</label>
                  <input value={fields.date} onChange={(e) => setFields({...fields, date: e.target.value})}
                    placeholder="Sun 12 May"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="invitation-date-input" maxLength={40} />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#475569] block mb-1.5"><Clock size={11} className="inline mr-1" /> Time</label>
                  <input value={fields.time} onChange={(e) => setFields({...fields, time: e.target.value})}
                    placeholder="7:00 PM onwards"
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="invitation-time-input" maxLength={30} />
                </div>
              </div>

              {/* Venue */}
              <div>
                <label className="text-xs font-bold text-[#475569] block mb-1.5"><MapPin size={11} className="inline mr-1" /> Venue</label>
                <input value={fields.venue} onChange={(e) => setFields({...fields, venue: e.target.value})}
                  placeholder="123 Sunshine Apartments, Bandra West"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="invitation-venue-input" maxLength={120} />
              </div>

              {/* Note */}
              <div>
                <label className="text-xs font-bold text-[#475569] block mb-1.5">Custom Note</label>
                <textarea value={fields.note} onChange={(e) => setFields({...fields, note: e.target.value})}
                  placeholder="A short message for your guests…"
                  rows={2}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm resize-none" data-testid="invitation-note-input" maxLength={200} />
              </div>

              {/* Style */}
              <div>
                <label className="text-xs font-bold text-[#475569] block mb-1.5">Vibe</label>
                <div className="flex flex-wrap gap-1.5">
                  {STYLES.map(s => (
                    <button key={s} onClick={() => setFields({...fields, style: s})}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${fields.style === s ? "bg-[#1E1B4B] text-white" : "bg-gray-100 text-[#475569] hover:bg-gray-200"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {error && <p className="text-xs text-red-500 font-medium" data-testid="invitation-error">{error}</p>}

              <button onClick={() => callGenerate(null)} disabled={generating}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-sm shadow-lg disabled:opacity-50 hover:opacity-95 transition-all flex items-center justify-center gap-2"
                data-testid="invitation-generate-btn">
                {generating ? (<><Loader2 size={16} className="animate-spin" /> Crafting your invitation...</>) : (<><Wand2 size={16} /> Generate Invitation</>)}
              </button>
              <p className="text-[10px] text-center text-[#475569]">First preview is on us. ✨ Powered by DoozyGo® Magic.</p>
            </div>
          )}

          {/* RIGHT: Live preview / result */}
          <div className={result ? "lg:col-span-2" : ""}>
            {result ? (
              <div className="bg-white rounded-3xl shadow-xl p-5 sm:p-7 space-y-4" data-testid="invitation-result">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-[10px] font-bold tracking-widest text-emerald-600 uppercase">✓ Generated</p>
                    <h3 className="text-xl font-black text-[#1E1B4B]">Your AI-crafted invitation</h3>
                  </div>
                  <button onClick={() => setResult(null)}
                    className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-[#1E1B4B] text-xs font-bold inline-flex items-center gap-1.5"
                    data-testid="invitation-new-btn">
                    <RefreshCw size={12} /> New Invitation
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  {/* Image */}
                  <div className="rounded-2xl overflow-hidden border-2 border-gray-100 bg-gray-50 max-w-md mx-auto">
                    <AIImageWithDisclaimer src={`data:image/png;base64,${result.image_base64}`} alt="AI invitation"
                      className="w-full h-auto" data-testid="invitation-image" />
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <button onClick={shareWhatsapp}
                      className="w-full py-3 rounded-2xl bg-[#25D366] text-white font-bold text-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      data-testid="invitation-share-wa">
                      <Share2 size={14} /> Share on WhatsApp
                    </button>
                    <button onClick={downloadImage}
                      className="w-full py-3 rounded-2xl bg-[#1E1B4B] text-white font-bold text-sm hover:opacity-90 transition flex items-center justify-center gap-2"
                      data-testid="invitation-download">
                      <Download size={14} /> Download PNG
                    </button>

                    {/* Alteration */}
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FFE5F0] to-[#FFF4E5] border border-[#E83E8C]/15">
                      <p className="text-xs font-bold text-[#1E1B4B] mb-1 flex items-center gap-1.5">
                        <Edit3 size={12} className="text-[#E83E8C]" /> Want changes?
                      </p>
                      <p className="text-[10px] text-[#475569] mb-2">
                        {result.free_remaining > 0
                          ? `${result.free_remaining} free alteration${result.free_remaining !== 1 ? "s" : ""} left — tweak fields above and re-generate.`
                          : result.paid_credits > 0
                            ? `${result.paid_credits} paid credit${result.paid_credits !== 1 ? "s" : ""} available.`
                            : "Out of free alterations. Buy a credit pack to keep editing."}
                      </p>
                      {(result.free_remaining > 0 || result.paid_credits > 0) ? (
                        <button onClick={() => callGenerate(result.id)} disabled={generating}
                          className="w-full py-2.5 rounded-xl bg-[#E83E8C] text-white text-xs font-bold hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                          data-testid="invitation-alter-btn">
                          {generating ? <><Loader2 size={12} className="animate-spin" /> Re-generating…</> : <><Wand2 size={12} /> Generate Alteration</>}
                        </button>
                      ) : (
                        <button onClick={() => setPaywall(true)}
                          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
                          <Lock size={12} /> Buy Credits
                        </button>
                      )}
                    </div>

                    {!usage?.is_logged_in && (
                      <LeadCaptureCard source="invitation_ai"
                        extra={{ occasion: fields.occasion, host_name: fields.host_name }}
                        headline="Get ₹500 off your party booking"
                        subheadline="Drop your number — we'll WhatsApp you a custom party setup quote within 24h."
                        cta="Get My Quote" />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-7 sticky top-20 hidden lg:block">
                <h3 className="text-lg font-bold text-[#1E1B4B] mb-3 flex items-center gap-2">
                  <Sparkles size={16} className="text-[#FF9F1C]" /> What you'll get
                </h3>
                <ul className="space-y-3 text-sm text-[#475569]">
                  <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> Photo-realistic AI invitation card</li>
                  <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> Fully personalised with your details</li>
                  <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> WhatsApp + Instagram ready (1080×1350)</li>
                  <li className="flex items-start gap-2"><Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" /> Saved to your "My Creations" gallery forever</li>
                  <li className="flex items-start gap-2"><Zap size={14} className="text-[#FF9F1C] mt-0.5 flex-shrink-0" /> 1 preview free · 2 free alterations for booked customers · ₹49 / ₹99 / ₹199 packs after</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Paywall modal */}
        {paywall && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPaywall(false)}>
            <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="invitation-paywall">
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] mb-2">
                  <Wand2 size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-black text-[#1E1B4B]">Unlock more alterations</h3>
                <p className="text-xs text-[#475569] mt-1">Each pack tops up your credit balance — credits never expire.</p>
              </div>
              <div className="space-y-2.5">
                {[
                  { amount: 49, credits: 1, label: "1 alteration", popular: false },
                  { amount: 99, credits: 3, label: "3 alterations", popular: true, save: "Save 33%" },
                  { amount: 199, credits: 10, label: "10 alterations", popular: false, save: "Save 60%" },
                ].map(p => (
                  <button key={p.amount} onClick={() => buyCredits(p.amount)} disabled={purchasing}
                    className={`w-full p-4 rounded-2xl border-2 text-left flex items-center justify-between transition-all hover:shadow-md disabled:opacity-50 ${p.popular ? "border-[#E83E8C] bg-gradient-to-r from-[#FFE5F0] to-white" : "border-gray-200 hover:border-[#E83E8C]/40"}`}
                    data-testid={`buy-pack-${p.amount}`}>
                    <div>
                      <p className="font-black text-[#1E1B4B] text-base">₹{p.amount}</p>
                      <p className="text-[11px] text-[#475569]">{p.label}{p.save ? ` · ${p.save}` : ""}</p>
                    </div>
                    {p.popular && <span className="px-2 py-0.5 rounded-full bg-[#E83E8C] text-white text-[9px] font-bold">POPULAR</span>}
                  </button>
                ))}
              </div>
              <button onClick={() => setPaywall(false)}
                className="mt-4 w-full py-2 text-xs text-[#475569] hover:text-[#1E1B4B]">
                Maybe later
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInvitationsPage;
